import React from 'react';
import { C, DIAMOND_W, DIAMOND_H, nx, ny } from '../flowCanvas.constants';

// ─── DiamondNode — decision / conditional block ──────────────────────────────
//
// Rendered as an SVG diamond (rotated square).  The nodeConfig in the seeder
// can supply:
//   nodeConfig.decisionDataPath   – dot-path into liveCustomisationData that
//                                   resolves the live boolean (e.g. "setup.settings.game.0.watchMixup")
//   nodeConfig.decisionLabel      – label shown when enabled  (default "Enabled")
//   nodeConfig.disabledLabel      – label shown when disabled (default "Disabled")
//
// If decisionDataPath is absent the badge is omitted and only the title shows.

export default function DiamondNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onToggle,
}) {
  const cx = overrideX ?? nx(step);
  const cy = overrideY ?? ny(step, centreY);

  const hw = DIAMOND_W / 2;   // half-width  of bounding box
  const hh = DIAMOND_H / 2;   // half-height of bounding box

  // Diamond corners (clockwise from top)
  const pts = `${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`;

  // ── Live decision value ─────────────────────────────────────────────────────
  const dataPath   = step.nodeConfig?.decisionDataPath;
  const hasPath    = !!dataPath;
  let   isEnabled  = null; // null = unknown / no path

  if (hasPath && liveCustomisationData) {
    const keys   = dataPath.split('.');
    let   target = liveCustomisationData;
    for (let i = 0; i < keys.length - 1; i++) {
      if (target == null) { target = null; break; }
      const k = keys[i];
      target = isNaN(k) ? target[k] : target[parseInt(k, 10)];
    }
    const leaf   = keys[keys.length - 1];
    const valObj = Array.isArray(target) ? target[0] : target;
    const val    = valObj?.[leaf];
    // Default to true (enabled) if the path is set but data hasn't loaded yet
    isEnabled = (val !== undefined && val !== null) ? !!val : true;
  } else if (hasPath) {
    // Path specified but no liveCustomisationData yet — default to enabled
    isEnabled = true;
  }

  const enabledLabel  = step.nodeConfig?.enabledLabel  || 'Enabled';
  const disabledLabel = step.nodeConfig?.disabledLabel || 'Disabled';

  // ── Colour logic ────────────────────────────────────────────────────────────
  const borderColor = selected
    ? C.sel
    : isEnabled === true
      ? C.green
      : isEnabled === false
        ? C.textDim
        : C.purple;

  const fillColor = selected ? C.cardSel : C.card;

  // ── Text wrapping ─────────────────────────────────────────────────────────
  const title = (step.stepTitle || '').trim();
  const words = title.split(/\s+/);
  const MAX_W = 88;  // safe inner width for square 140px diamond
  const estimateW = (t) => t.length * 6.5;

  const wrapToLines = (ws, max) => {
    const lines = [];
    let cur = '';
    ws.forEach(w => {
      const next = cur ? `${cur} ${w}` : w;
      if (estimateW(next) <= max) { cur = next; }
      else { if (cur) lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    return lines.slice(0, 3);
  };

  const lines  = wrapToLines(words, MAX_W);
  const lineH  = 13;

  // Vertical layout: text block + toggle pill, centered in the diamond
  const TOGGLE_H   = 22;
  const TOGGLE_W   = 64;
  const GAP        = 8;
  const totalH     = lines.length * lineH + (hasPath ? GAP + TOGGLE_H : 0);
  const textStartY = cy - totalH / 2 + lineH / 2;
  const toggleY    = textStartY + lines.length * lineH + GAP;
  const toggleCY   = toggleY + TOGGLE_H / 2;
  const dotX       = isEnabled ? cx + TOGGLE_W / 2 - 13 : cx - TOGGLE_W / 2 + 13;

  // ── Small ◆ icon top-right of diamond ──────────────────────────────────────
  const iconX = cx + hw - 14;
  const iconY = cy - hh + 14;

  return (
    <g
      className="fc-node fc-node--decision"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Drop shadow */}
      <polygon
        points={`${cx},${cy - hh + 4} ${cx + hw + 3},${cy + 4} ${cx},${cy + hh + 4} ${cx - hw + 3},${cy + 4}`}
        fill="#000"
        fillOpacity="0.25"
      />

      {/* Diamond body */}
      <polygon
        points={pts}
        fill={fillColor}
        stroke={borderColor}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* Subtle inner diamond outline */}
      <polygon
        points={`${cx},${cy - hh + 6} ${cx + hw - 10},${cy} ${cx},${cy + hh - 6} ${cx - hw + 10},${cy}`}
        fill="none"
        stroke={borderColor}
        strokeWidth="0.5"
        strokeOpacity="0.3"
      />

      {/* ◆ decision icon top-right */}
      <text
        x={iconX} y={iconY}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="9"
        fill={borderColor}
        fillOpacity="0.7"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >◆</text>

      {/* Title lines */}
      {lines.map((line, i) => (
        <text
          key={i}
          x={cx}
          y={textStartY + i * lineH}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="10.5"
          fontWeight="700"
          fill={C.text}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >{line}</text>
      ))}

      {/* Toggle pill — clickable, stops propagation so node-select is unaffected */}
      {hasPath && isEnabled !== null && (
        <g
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          style={{ cursor: 'pointer' }}
        >
          {/* Pill track */}
          <rect
            x={cx - TOGGLE_W / 2} y={toggleY}
            width={TOGGLE_W} height={TOGGLE_H}
            rx={TOGGLE_H / 2}
            fill={isEnabled ? C.green : '#374151'}
          />
          {/* Label */}
          <text
            x={isEnabled ? cx - 10 : cx + 10}
            y={toggleCY}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="8" fontWeight="700" fill="white"
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >{isEnabled ? enabledLabel : disabledLabel}</text>
          {/* Dot */}
          <circle cx={dotX} cy={toggleCY} r={8} fill="white" />
        </g>
      )}
    </g>
  );
}
